// =============================================================
// DamDownloadModal.jsx — choose a download size for an image asset.
//
// Mirrors the size chooser on the public share page (PublicShare.jsx) but for
// authenticated internal users: download the Original, or a resized copy by
// pixel dimensions or by target file size (MB). Non-image assets never open
// this — they download the original directly (see DamLibrary / DamAssetModal).
//
// Part of the removable DAM module.
// =============================================================
import { useState } from 'react';
import { B, space, font, radius, weight } from '../../constants/brand';
import { Modal, Btn, Icon, useToast } from '../../components/ui';
import { formatBytes, downloadAsset } from './damApi';
import { sanitizeNumericInput, WHOLE, UNSIGNED } from '../../utils/numericInput';

const PRESET_DIMS = [480, 1024, 1920, 2560];
const PRESET_MB = [0.5, 1, 2, 5];

export function DamDownloadModal({ asset, onClose }) {
  const { toast } = useToast();
  const [mode, setMode] = useState('original');   // original | dims | size
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [maxMB, setMaxMB] = useState('');
  const [busy, setBusy] = useState(false);

  const canSubmit = mode === 'original'
    || (mode === 'dims' && (width || height))
    || (mode === 'size' && maxMB);

  const doDownload = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      let opts = {};
      if (mode === 'dims') opts = { width: width || undefined, height: height || undefined };
      else if (mode === 'size') opts = { maxMB: maxMB || undefined };
      await downloadAsset(asset.assetID, opts);
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Download" onClose={onClose} width={520}>
      <div style={{ padding: space.xl }}>
        <div style={{ fontSize: font.base, fontWeight: weight.semibold, color: B.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {asset.title || asset.fileName}
        </div>
        <div style={{ marginTop: 4, fontSize: font.sm, color: B.textMd, display: 'flex', gap: space.lg, flexWrap: 'wrap' }}>
          {asset.fileSizeBytes != null && <span>{formatBytes(asset.fileSizeBytes)}</span>}
          {asset.width && asset.height ? <span>{asset.width} × {asset.height}px</span> : null}
        </div>

        <div style={{ marginTop: space.lg, padding: space.lg, background: B.surfaceMuted,
          borderRadius: radius.xxl, border: `1px solid ${B.divider}` }}>
          <div style={{ fontSize: font.sm, fontWeight: weight.semibold, color: B.text, marginBottom: space.sm }}>
            Choose a size
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: space.md }}>
            {[['original', 'Original'], ['dims', 'By dimensions (px)'], ['size', 'By file size (MB)']].map(([id, label]) => (
              <button key={id} onClick={() => setMode(id)} style={{
                padding: '6px 12px', borderRadius: radius.pill, cursor: 'pointer', fontFamily: 'inherit',
                fontSize: font.sm, fontWeight: weight.medium,
                border: `1px solid ${mode === id ? B.navy : B.border}`,
                background: mode === id ? B.navy : B.surface, color: mode === id ? B.white : B.textMd,
              }}>{label}</button>
            ))}
          </div>

          {mode === 'dims' && (
            <div>
              <div style={{ display: 'flex', gap: space.sm, alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="text" inputMode="numeric" value={width} onChange={e => setWidth(sanitizeNumericInput(e.target.value, WHOLE))} placeholder="Width" style={numInput} />
                <span style={{ color: B.textLt }}>×</span>
                <input type="text" inputMode="numeric" value={height} onChange={e => setHeight(sanitizeNumericInput(e.target.value, WHOLE))} placeholder="Height" style={numInput} />
                <span style={{ fontSize: font.xs, color: B.textLt }}>px</span>
              </div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: space.sm }}>
                {PRESET_DIMS.map(px => (
                  <button key={px} onClick={() => { setWidth(String(px)); setHeight(''); }} style={presetBtn}>{px}px wide</button>
                ))}
              </div>
              <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 6 }}>
                Leave one field blank to keep the aspect ratio. Images are only scaled down, never up.
              </div>
            </div>
          )}

          {mode === 'size' && (
            <div>
              <div style={{ display: 'flex', gap: space.sm, alignItems: 'center' }}>
                {/* Sub-1 MB is the designed case (min 0.05, and 0.5 is a
                    preset), so ".5" is how anyone types half a megabyte — and
                    the Download button is gated on this box being non-empty,
                    with nothing on screen if it is not. What a number input
                    does to a leading point is engine-specific, so it should not
                    be what decides whether the button works. */}
                <input type="text" inputMode="decimal" value={maxMB} onChange={e => setMaxMB(sanitizeNumericInput(e.target.value, UNSIGNED))} placeholder="Max size" style={numInput} />
                <span style={{ fontSize: font.xs, color: B.textLt }}>MB</span>
              </div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: space.sm }}>
                {PRESET_MB.map(mb => (
                  <button key={mb} onClick={() => setMaxMB(String(mb))} style={presetBtn}>{mb} MB</button>
                ))}
              </div>
              <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 6 }}>
                We'll compress the image to land under your target size.
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: space.sm,
        padding: `${space.md}px ${space.xl}px`, borderTop: `1px solid ${B.border}` }}>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn onClick={doDownload} disabled={!canSubmit} loading={busy}>
          <Icon name="download" size={15} /> Download{mode !== 'original' ? ' resized' : ''}
        </Btn>
      </div>
    </Modal>
  );
}

const numInput = {
  width: 110, padding: '8px 10px', fontSize: font.base, fontFamily: 'inherit',
  border: `1px solid ${B.border}`, borderRadius: radius.md, color: B.text, background: B.surface, outline: 'none',
};
const presetBtn = {
  padding: '4px 10px', borderRadius: radius.pill, cursor: 'pointer', fontFamily: 'inherit',
  fontSize: font.xs, fontWeight: weight.medium, border: `1px solid ${B.border}`, background: B.surface, color: B.textMd,
};
